import { Box, Typography } from '@mui/material';

import { FHSelect } from '../../../../../../../../../../base';
import ErrorBox from '../../../../../errorBox';
import NumericInput from '../../../../../../components/numericInput';
import {
  FORTA_BLACK,
  FORTA_ERROR,
  FORTA_GRAY2,
} from '../../../../../../../../../../../utils/colors';
import { BackgroundTypes, Themes } from '../../../../../../../../../../../constants';
import { validateInstanceMasteryCriteria } from '../../../../../../../common/utils/general';
import { FREQUENCY_OPTIONS } from '../../../common/constants/general';

import type { TreatmentPlanResourceVersion } from '../../../../../../constants/general';
import type { SelectValueType } from '../../../../../../../../../../../utils/types';
import type { ISingleRowInstanceMasteryCriteriaState } from '../../../singleRowInstanceMasteryCriteria';

interface IInstanceMCRowProps {
  isEditing: boolean;
  goalIndex: number;
  goalVersion: TreatmentPlanResourceVersion;
  values: ISingleRowInstanceMasteryCriteriaState;
  title?: string;
  showFrequency?: boolean;
  baseline_at_creation: number | null;
  maintenance_frequency?: string | null;
  isPCGGoal?: boolean;
  isEditable: boolean;
  updateMaintenanceFrequency?: (value: string) => void;
  onUpdate: (key: string, value: number | string | null) => void;
}

function InstanceMCRow({
  isEditing,
  goalIndex,
  goalVersion,
  values,
  title,
  showFrequency,
  baseline_at_creation,
  maintenance_frequency,
  isPCGGoal,
  isEditable,
  updateMaintenanceFrequency,
  onUpdate,
}: IInstanceMCRowProps) {
  const errors = validateInstanceMasteryCriteria(
    values,
    baseline_at_creation,
    goalVersion,
  );
  const hasErrors = Object.keys(errors).length > 0;

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        border: `1px solid ${hasErrors ? FORTA_ERROR : FORTA_GRAY2}`,
        borderRadius: '12px',
      }}
    >
      {title && (
        <Typography textAlign="left" fontWeight={700}>
          {title}
        </Typography>
      )}
      {hasErrors && (
        <Typography textAlign="left" color={FORTA_ERROR} mt={2} mb={1}>
          {errors.desired_amount || errors.practice_duration_count || errors.baseline_at_creation}
        </Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography textAlign="left" display="inline">
          {isPCGGoal ? "Client's parent will " : 'Client will'}
        </Typography>
        {isEditing ? (
          <FHSelect
            data-testid={`masterCriteriaChangeType-${goalIndex}`}
            sx={{ minWidth: '81px', mx: 1, borderRadius: '6px' }}
            inputHeight={40}
            colorTheme={Themes.Primary}
            backgroundType={BackgroundTypes.Colored}
            options={[
              { value: 'decrease', label: 'decrease' },
              { value: 'increase', label: 'increase' },
            ]}
            value={values.change_type ? values.change_type : ''}
            onUpdate={(value: SelectValueType) => {
              onUpdate('change_type', value);
            }}
            error={Boolean(errors.change_type)}
          />
        ) : values.change_type ? (
          <Typography textAlign="left" sx={{ mx: 0.5 }}>{` ${values.change_type} `}</Typography>
        ) : (
          <ErrorBox width={70} sx={{ mx: 0.5 }} />
        )}
        <Typography textAlign="left" display="inline" sx={{ mr: 0.5 }}>
          instances of the target skill/target behavior from
        </Typography>
        <Typography
          textAlign="left"
          display="inline"
          fontWeight={700}
          color={baseline_at_creation || baseline_at_creation === 0 ? FORTA_BLACK : FORTA_ERROR}
          sx={{ ml: 0.5 }}
        >
          {baseline_at_creation || baseline_at_creation === 0 ? baseline_at_creation : '?'}
        </Typography>

        <Typography sx={{ ml: 0.5 }}>to</Typography>
        {isEditing ? (
          <NumericInput
            testId={`masterCriteriaDesiredLevel`}
            sx={{
              width: 45,
              minWidth: 45,
              borderRadius: 1.5,
              ml: 0.5,
            }}
            value={values.desired_amount}
            onChange={(value) => {
              onUpdate('desired_amount', value ?? '');
            }}
            error={
              values.desired_amount === null ||
              values.desired_amount === undefined ||
              Boolean(errors.desired_amount)
            }
          />
        ) : values.desired_amount || values.desired_amount === 0 ? (
          <Typography textAlign="left" fontWeight={700} display="inline" sx={{ ml: 0.5 }}>
            {values.desired_amount}
          </Typography>
        ) : (
          <ErrorBox width={40} sx={{ mx: 0.5 }} />
        )}
        <Typography textAlign="left" display="inline" sx={{ ml: 1 }}>
          instances per session across{' '}
        </Typography>
        {isEditing ? (
          <NumericInput
            testId={`masterCriteriaPracticeDurationCount-${goalIndex}`}
            sx={{
              width: 45,
              minWidth: 45,
              borderRadius: 1.5,
              ml: 0.5,
            }}
            value={values.practice_duration_count}
            onChange={(value) => {
              onUpdate('practice_duration_count', value ?? '');
            }}
            error={
              values.practice_duration_count === null ||
              values.practice_duration_count === undefined ||
              Boolean(errors.practice_duration_count)
            }
          />
        ) : values.practice_duration_count || values.practice_duration_count === 0 ? (
          <Typography textAlign="left" display="inline" fontWeight={700} sx={{ mx: 0.5 }}>
            {values.practice_duration_count}
          </Typography>
        ) : (
          <ErrorBox width={40} sx={{ mx: 0.5 }} />
        )}
        <Typography textAlign="left" display="inline" sx={{ ml: 1 }}>
          consecutive sessions
        </Typography>
      </Box>
      {showFrequency && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            my: 1,
          }}
        >
          <Typography
            sx={{
              textAlign: 'left',
              fontWeight: 700,
              mb: 1,
            }}
          >
            Frequency
          </Typography>
          {isEditing ? (
            <FHSelect
              data-testid={`maintenanceFrequencyDropDown`}
              sx={{ minWidth: 200 }}
              inputHeight={40}
              backgroundType={BackgroundTypes.Colored}
              displayEmpty
              disabled={!isEditable}
              renderValue={
                maintenance_frequency === null
                  ? () => <Typography>Select frequency</Typography>
                  : undefined
              }
              options={FREQUENCY_OPTIONS}
              optionDataTestIdPrefix="maintenanceFrequencyOption"
              value={maintenance_frequency || ''}
              onUpdate={(value: SelectValueType) => {
                const selectedFrequency = FREQUENCY_OPTIONS.find(
                  (option) => option.value === value,
                )?.value;
                if (updateMaintenanceFrequency && selectedFrequency) {
                  updateMaintenanceFrequency(selectedFrequency);
                }
              }}
              error={!maintenance_frequency}
            />
          ) : (
            <Typography align="left">{maintenance_frequency ?? ''}</Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

export default InstanceMCRow;
